import "./style.scss";
import type { Control, FieldValues, Path, UseFormClearErrors, UseFormRegister, UseFormSetError } from "react-hook-form";
import { Controller } from "react-hook-form";
import CustomSwitch from "@/src/components/CustomUtils/CustomSwitch/CustomSwitch";
import AOPTaskIdInput from "@/src/components/AdmissionsOfficePage/shared/AOPTaskIdInput/AOPTaskIdInput";
import AOPStudentIdInput from "@/src/components/AdmissionsOfficePage/shared/AOPStudentIdInput/AOPStudentIdInput";
import type { AdmissionTaskPrefillPayload } from "@/src/hooks/useAdmissionTaskPrefill";
import { AODSvgExport } from "@/src/svg/admissionOfficePage/AOPSvgExport";
import SearchSvg from "@/src/svg/default/SearchSvg";

interface AOPIdSourceRowProps<TForm extends FieldValues> {
  control: Control<TForm>;
  register: UseFormRegister<TForm>;
  setError: UseFormSetError<TForm>;
  clearErrors: UseFormClearErrors<TForm>;
  applyTaskPrefill: (payload: AdmissionTaskPrefillPayload) => void;
  lastPrefilledTaskIdRef: React.MutableRefObject<string>;
  lastPrefilledStudentIdRef: React.MutableRefObject<string>;
  fieldEmail: boolean;
  isTaskIdDisabled: boolean;
  isStudentIdDisabled: boolean;
  isFieldEmailDisabled?: boolean;
  onClearTaskId: () => void;
  onClearStudentId: () => void;
  onClearUserEmail: () => void;
  onCheckUserEmail: () => void;
  onCopy: (fieldName: Path<TForm>) => void;
}

const AOPIdSourceRow = <TForm extends FieldValues>({
  control,
  register,
  setError,
  clearErrors,
  applyTaskPrefill,
  lastPrefilledTaskIdRef,
  lastPrefilledStudentIdRef,
  fieldEmail,
  isTaskIdDisabled,
  isStudentIdDisabled,
  isFieldEmailDisabled = false,
  onClearTaskId,
  onClearStudentId,
  onClearUserEmail,
  onCheckUserEmail,
  onCopy,
}: AOPIdSourceRowProps<TForm>) => {
  return (
    <div className="admission-office-page-config-id-row">
      <div className="form-group-fields admission-office-page-config-id-row-main">
        <AOPTaskIdInput
          register={register}
          setError={setError}
          clearErrors={clearErrors}
          applyTaskPrefill={applyTaskPrefill}
          lastPrefilledTaskIdRef={lastPrefilledTaskIdRef}
          disabled={isTaskIdDisabled}
          onClear={onClearTaskId}
          onCopy={() => onCopy("task_id" as Path<TForm>)}
        />
        <AOPStudentIdInput
          register={register}
          setError={setError}
          clearErrors={clearErrors}
          applyTaskPrefill={applyTaskPrefill}
          lastPrefilledStudentIdRef={lastPrefilledStudentIdRef}
          disabled={isStudentIdDisabled}
          onClear={onClearStudentId}
          onCopy={() => onCopy("student_id" as Path<TForm>)}
        />
        <div className="form-group-fields-invoice">
          <Controller
            name={"fieldEmail" as Path<TForm>}
            control={control}
            render={({ field }) => (
              <CustomSwitch
                label="Field email"
                value={!!field.value}
                disabled={isFieldEmailDisabled}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </div>

      <div className={`admission-office-page-config-id-row-email${fieldEmail ? " is-open" : ""}`}>
        <div className="admission-office-page-config-id-row-email-inner">
          <div className="form-group-fields form-group-fields-full">
            <div className="form-group-fields-input">
              <div className="form-group-fields-input-label">
                <label htmlFor="userEmail">Email</label>
                <div className="form-group-fields-input-label-line"></div>
              </div>
              <input
                className="userEmail"
                id="userEmail"
                type="text"
                {...register("userEmail" as Path<TForm>)}
                disabled={!fieldEmail}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  onCheckUserEmail();
                }}
              />
              <div className="form-group-fields-input-svg">
                <div className="form-group-fields-input-svg-delete" onClick={onClearUserEmail}>
                  <AODSvgExport.TrashSvg />
                </div>
                <div className="form-group-fields-input-svg-line"></div>
                <div
                  className="form-group-fields-input-svg-copy"
                  onClick={() => onCopy("userEmail" as Path<TForm>)}
                >
                  <AODSvgExport.FullNameCopySvg />
                </div>
                <div className="form-group-fields-input-svg-line"></div>
                <div className="form-group-fields-input-svg-check" onClick={onCheckUserEmail}>
                  <SearchSvg />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AOPIdSourceRow;
